import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, formatBytes } from '../lib/api.js';

type Storage = Awaited<ReturnType<typeof api.adminStorage>>;

function DiskBar({
  label,
  disk,
}: {
  label: string;
  disk: Storage['nas'];
}): ReactNode {
  if (!disk.available) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div className="flex">
          <strong style={{ fontSize: 13 }}>{label}</strong>
          <div className="spacer" />
          <span style={{ color: 'var(--danger)', fontSize: 12 }}>UNAVAILABLE</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{disk.path}</div>
      </div>
    );
  }

  // Colour by headroom rather than a fixed threshold: running out of space is
  // the failure this whole system exists to avoid.
  const colour =
    disk.percentUsed >= 90
      ? 'var(--danger)'
      : disk.percentUsed >= 75
        ? 'var(--warning)'
        : 'var(--success)';

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="flex" style={{ marginBottom: 4 }}>
        <strong style={{ fontSize: 13 }}>{label}</strong>
        <div className="spacer" />
        <span style={{ fontSize: 12 }}>
          <span style={{ color: colour, fontWeight: 600 }}>
            {formatBytes(disk.freeBytes)} free
          </span>
          <span style={{ color: 'var(--text-dim)' }}>
            {' '}
            of {formatBytes(disk.totalBytes)} ({disk.percentUsed}% used)
          </span>
        </span>
      </div>
      <div className="progress" style={{ height: 8 }}>
        <div style={{ width: `${disk.percentUsed}%`, background: colour }} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{disk.path}</div>
    </div>
  );
}

export function StoragePanel(): ReactNode {
  const [data, setData] = useState<Storage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .adminStorage()
      .then(setData)
      .catch((err: Error) => setError(err.message));
  }, []);

  if (error) return <div className="notice error">{error}</div>;
  if (!data) return <div className="empty">Loading…</div>;

  const { projection, duplication } = data;
  const maxDay = Math.max(...data.growth.map((g) => Number(g.bytes)), 1);

  return (
    <>
      <div className="panel">
        <h3>Disk space</h3>
        <DiskBar label="NAS — originals" disk={data.nas} />
        <DiskBar label="Local — thumbnails, proxies, uploads in flight" disk={data.cache} />

        {projection.daysUntilFull !== null && (
          <div
            className={`notice${projection.daysUntilFull < 60 ? ' error' : ''}`}
            style={{ marginBottom: 0 }}
          >
            Averaging {formatBytes(projection.bytesPerDay)}/day over the last 30 days.
            {projection.daysUntilFull > 3650
              ? ' The NAS has years of headroom at this rate.'
              : ` At that rate the NAS fills in roughly ${projection.daysUntilFull} days.`}
          </div>
        )}
      </div>

      <div className="panel">
        <h3>Archive</h3>
        <dl className="kv">
          <dt>Files</dt>
          <dd>{data.archive?.files.toLocaleString() ?? 0}</dd>
          <dt>Size</dt>
          <dd>{formatBytes(Number(data.archive?.bytes ?? 0))}</dd>
          {duplication && duplication.saved > 0 && (
            <>
              <dt>Saved by dedupe</dt>
              <dd title="Identical files are stored once, however many times they were uploaded">
                {formatBytes(Number(duplication.saved))}
                <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>
                  {' '}
                  — {duplication.total_rows - duplication.unique_files} duplicate upload(s) cost nothing
                </span>
              </dd>
            </>
          )}
          <dt>In trash</dt>
          <dd>
            {data.trash?.files ?? 0} files, {formatBytes(Number(data.trash?.bytes ?? 0))}
            {Number(data.trash?.reclaimable ?? 0) > 0 && (
              <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>
                {' '}
                — {formatBytes(Number(data.trash?.reclaimable))} would actually be freed
              </span>
            )}
          </dd>
        </dl>

        <table style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Type</th>
              <th style={{ width: 90 }}>Files</th>
              <th style={{ width: 110 }}>Size</th>
            </tr>
          </thead>
          <tbody>
            {data.byKind.map((row) => (
              <tr key={row.kind}>
                <td>{row.kind}</td>
                <td>{row.files.toLocaleString()}</td>
                <td>{formatBytes(Number(row.bytes))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.growth.length > 0 && (
        <div className="panel">
          <h3>Uploads, last 30 days</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 90 }}>
            {data.growth.map((day) => (
              <div
                key={day.day}
                title={`${day.day}: ${day.files} files, ${formatBytes(Number(day.bytes))}`}
                style={{
                  flex: 1,
                  minWidth: 3,
                  height: `${Math.max((Number(day.bytes) / maxDay) * 100, 2)}%`,
                  background: 'var(--accent)',
                  borderRadius: '2px 2px 0 0',
                }}
              />
            ))}
          </div>
          <div
            className="flex"
            style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}
          >
            <span>{data.growth[0]?.day}</span>
            <div className="spacer" />
            <span>peak {formatBytes(maxDay)}/day</span>
            <div className="spacer" />
            <span>{data.growth.at(-1)?.day}</span>
          </div>
        </div>
      )}

      {data.largestFolders.length > 0 && (
        <div className="panel">
          <h3>Largest folders</h3>
          <table>
            <thead>
              <tr>
                <th>Folder</th>
                <th style={{ width: 80 }}>Files</th>
                <th style={{ width: 110 }}>Size</th>
              </tr>
            </thead>
            <tbody>
              {data.largestFolders.map((folder) => (
                <tr key={folder.id}>
                  <td>
                    <Link to={`/folder/${folder.id}`} style={{ color: 'var(--accent)' }}>
                      {folder.path}
                    </Link>
                  </td>
                  <td>{folder.files.toLocaleString()}</td>
                  <td>{formatBytes(Number(folder.bytes))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
